import { Button, Dialog, Field, Icon, Select } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { MATRIX_REASONS, NOTE_MINIMUM } from './vocabulary.ts';
import type { PermissionDef, RoleDef } from './types.ts';

/**
 * The confirmation behind one matrix cell.
 *
 * "Click a cell to grant or remove" is the design's hint and it is now true —
 * but a bare click is the wrong shape for this particular write. Every other
 * control in this module changes what ONE person may do; this one changes what
 * a CLASS of people may do, immediately, at their next request. So it takes the
 * same reason code and note as a suspension, states how many accounts it
 * reaches, and says which way the change goes in words rather than leaving the
 * operator to remember which state the cell was in when they pressed it.
 *
 * ── WHY "AT THEIR NEXT REQUEST" IS AN HONEST PROMISE ───────────────────────
 *
 * Both notices promise the change lands without anybody being signed out and
 * without anybody signing in again, and that promise only holds because nothing
 * anywhere caches a capability set for the length of a session: the session
 * plugin selects identity.role_permission on every authenticated request and
 * rebuilds the allowed set from scratch. The table name is recorded here rather
 * than in the notice, because an operator can do nothing with it — what they
 * need is the EFFECT, that the platform works out what each person is allowed
 * to do fresh every time they ask for something — while whoever next touches
 * that plugin does need to know that this sentence on this screen depends on it
 * staying that way. If the lookup is ever cached per session, these two notices
 * become lies and have to be rewritten in the same change.
 */
export function MatrixCellDialog({
  target,
  saving,
  errors,
  reasonCode,
  onReasonCode,
  note,
  onNote,
  onSubmit,
  onClose,
}: {
  target: { role: RoleDef; permission: PermissionDef; grant: boolean } | null;
  saving: boolean;
  errors: Record<string, string>;
  reasonCode: string;
  onReasonCode: (v: string) => void;
  note: string;
  onNote: (v: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const verb = target?.grant ? 'Grant' : 'Take away';

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title={
        target ? `${verb} ${target.permission.label} — ${target.role.name}` : ''
      }
      wide
      dismissible={!saving}
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={saving}>
            {saving ? 'Saving…' : `${verb} capability`}
          </Button>
        </>
      }
    >
      {target && (
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name={target.grant ? 'info' : 'warning-circle'} />
            <div>
              {target.grant ? (
                <>
                  Every admin holding <strong>{target.role.name}</strong> —{' '}
                  {formatCount(target.role.adminCount)} account
                  {target.role.adminCount === 1 ? '' : 's'} — gains{' '}
                  <code>{target.permission.key}</code> at their <strong>next request</strong>.
                  Nobody is signed out and nobody signs in again: the platform works out what each
                  person is allowed to do fresh every time they ask for something, rather than
                  remembering it from when they signed in.
                </>
              ) : (
                <>
                  Every admin holding <strong>{target.role.name}</strong> —{' '}
                  {formatCount(target.role.adminCount)} account
                  {target.role.adminCount === 1 ? '' : 's'} — loses{' '}
                  <code>{target.permission.key}</code> at their <strong>next request</strong>,
                  including anyone with the screen open right now. The row is not deleted: it is
                  marked revoked, so the trail of who held what and when survives.
                </>
              )}
            </div>
          </div>

          {target.grant && target.permission.requiresTwoFactor && (
            <div className="notice notice-quiet">
              <Icon name="lock-simple" />
              <div>
                <code>{target.permission.key}</code> is only honoured from a session verified with
                two-factor authentication. If anyone holding {target.role.name} has not enrolled an
                authenticator, the platform refuses this and names them — they would otherwise appear
                authorised on the roster and be refused on every click.
              </div>
            </div>
          )}

          {target.grant && target.permission.writes && (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                This capability <strong>changes something</strong>. A read-only role may hold none
                that do, which the platform enforces — a view permission must never gate a write, and
                a write permission must never gate a view.
              </div>
            </div>
          )}

          <Field
            label="Reason"
            hint="A closed set: an access review asks how many grants were incident response and how many were somebody's new job, and prose cannot answer that."
            error={errors['reasonCode']}
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={reasonCode}
                onChange={(e) => onReasonCode(e.target.value)}
              >
                {MATRIX_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint={`At least ${NOTE_MINIMUM} characters. The audit row carries the whole grant set before and after; this is what says why it moved.`}
            error={errors['note']}
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={note}
                onChange={(e) => onNote(e.target.value)}
                placeholder="Who authorised this, and against what review or ticket"
              />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}
